import type { NetworkFields } from '@tribeplatform/gql-client/graphql'
import type {
  CustomFieldSchema,
  AuthToken,
  MutationReorderMemberSchemaFieldsArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useReorderMemberSchemaFields = (options?: {
  fields?: NetworkFields
  useMutationOptions?: UseMutationOptions<
    Network,
    Error,
    MutationReorderMemberSchemaFieldsArgs
  >
}) => {
  const { useMutationOptions, fields = 'default' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<Network, Error, MutationReorderMemberSchemaFieldsArgs>(
    input => client.network.reorderMemberSchemaFields(input, fields),
    {
      onMutate: variables => {
        queryClient.cancelQueries(networkKey)
        queryClient.cancelQueries(authTokenKey)

        const updater = (old: CustomFieldSchema[]) => {
          const inputFieldsByKey: Record<string, number> = {}
          variables.input.forEach(({ index, key }) => {
            inputFieldsByKey[key] = index
          })

          return old?.sort((a, b) => {
            return inputFieldsByKey[a.key] - inputFieldsByKey[b.key]
          })
        }

        queryClient.setQueriesData<AuthToken>(authTokenKey, old => ({
          ...old,
          network: {
            ...old?.network,
            memberFields: {
              fields: updater(old?.network?.memberFields?.fields),
            },
          },
        }))
        queryClient.setQueriesData<Network>(networkKey, old => {
          return {
            ...old,
            memberFields: {
              fields: updater(old?.memberFields?.fields),
            },
          }
        })
      },
      onSettled: () => {
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      ...useMutationOptions,
    },
  )
}
